import * as React from "react";

import { useHttpClient } from "@/providers/HttpClient";
import { type PaginatedResponse } from "@/shared/http";

import { AutocompleteType } from "./types";

// Safety bound on how many pages of a paginated tag corpus (the
// datasets/events/files `/tags` endpoints, which page at a 5000-item
// backend default) this hook will follow. Without a cap, an org with an
// unbounded tag corpus would keep this effect fetching indefinitely; 10
// pages caps the fetch at 50,000 tags, comfortably above any real org's
// corpus, while still fixing the regression where only the first page
// (≤5000 items) was ever surfaced.
const MAX_PAGINATED_TAG_PAGES = 10;
const MAX_PAGINATED_TAGS = 50_000;

const autocompleteTypeToUrl = {
  [AutocompleteType.ActionEnvVarKeys]: "",
  [AutocompleteType.ActionMetadataKeys]: "actions/metadata/keys",
  [AutocompleteType.ActionTags]: "actions/tags",
  [AutocompleteType.CollectionTags]: "collections/tags",
  [AutocompleteType.DatasetMetadataKeys]: "datasets/metadata/keys",
  [AutocompleteType.DatasetTags]: "datasets/tags",
  [AutocompleteType.EventMetadataKeys]: "events/metadata/keys",
  [AutocompleteType.EventNames]: "events/names",
  [AutocompleteType.EventTags]: "events/tags",
  [AutocompleteType.FileMetadataKeys]: "files/metadata/keys",
  [AutocompleteType.FileTags]: "files/tags",
  [AutocompleteType.GenericMetadataKeys]: "",
  [AutocompleteType.GenericTags]: "",
  // No `/v1/sessions/{tags,metadata/keys}` corpus endpoints exist; suggestions
  // come from the `existingTags`/`existingMetadata` props the autocomplete
  // already merges in.
  [AutocompleteType.SessionMetadataKeys]: "",
  [AutocompleteType.SessionTags]: "",
  [AutocompleteType.SkillTags]: "skills/tags",
  [AutocompleteType.TopicMetadataKeys]: "topics/metadata/keys",
};

interface ElementsForAutocompleteState {
  elements: string[];
  error: Error | null;
}

export const useElementsForAutocompleteType = (
  autocompleteType: AutocompleteType,
  existingElements: string[] = [],
): ElementsForAutocompleteState => {
  const [state, setState] = React.useState<ElementsForAutocompleteState>({
    elements: [],
    error: null,
  });

  const { httpClient } = useHttpClient();

  const arrayAsString = existingElements.sort().join();

  React.useEffect(() => {
    const urlPath = autocompleteTypeToUrl[autocompleteType];

    if (urlPath === "") {
      setState({
        elements: [],
        error: null,
      });
      return;
    }

    const abortController = new AbortController();

    const fetchElements = async () => {
      try {
        const elements: string[] = [];
        let pageToken: string | undefined;
        let pagesFetched = 0;

        do {
          const searchParams = new URLSearchParams();
          if (pageToken !== undefined) {
            searchParams.append("page_token", pageToken);
          }
          const url = httpClient.constructUrl(`v1/${urlPath}`, searchParams);
          const response = await httpClient.get(url, {
            signal: abortController.signal,
          });
          // The dataset/event/file tags endpoints return a paginated
          // `{ items, next_token }` object as of API version 2026-08-10; the
          // remaining corpus endpoints still return a bare array (and are
          // never paginated, so the loop below always exits after one pass).
          const body = await response.json<
            string[] | PaginatedResponse<string>
          >();
          pagesFetched += 1;

          if (Array.isArray(body)) {
            elements.push(...body);
            pageToken = undefined;
          } else {
            elements.push(...body.items);
            pageToken = body.next_token ?? undefined;
          }
        } while (
          pageToken !== undefined &&
          pagesFetched < MAX_PAGINATED_TAG_PAGES &&
          elements.length < MAX_PAGINATED_TAGS
        );

        setState({
          elements: elements,
          error: null,
        });
      } catch (e) {
        if (abortController.signal.aborted) {
          return;
        }

        setState({
          elements: [],
          error: new Error("Failed to fetch elements for autocomplete type", {
            cause: e,
          }),
        });
      }
    };

    void fetchElements();

    return () => {
      abortController.abort();
    };
  }, [httpClient, autocompleteType, arrayAsString]);

  return state;
};
